import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  addToQueue,
  getQueueCount,
  getQueueItems,
  markQueueItemSynced,
  removeQueueItem,
  clearSyncedItems,
  cacheWorkOrders,
  getCachedWorkOrders,
  getUnsyncedPhotos,
  markPhotoSynced
} from '../lib/offlineDB';
import { API_URL } from '../lib/config';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing changes...', { duration: 3000 });
      syncQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will sync when connected.', { duration: 5000 });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update queue count
  const updateQueueCount = useCallback(async () => {
    const count = await getQueueCount();
    setQueueCount(count);
  }, []);

  useEffect(() => {
    updateQueueCount();
  }, [updateQueueCount]);

  // Queue an API request for later sync
  const queueRequest = useCallback(async (request) => {
    const { method, url, data, description } = request;
    
    await addToQueue({
      type: 'api',
      method,
      url,
      data,
      description: description || `${method} ${url}`
    });
    
    await updateQueueCount();
    
    if (!isOnline) {
      toast.info(`Saved offline: ${description || 'Request queued'}`, { duration: 2000 });
    }
    
    return { queued: true };
  }, [isOnline, updateQueueCount]);

  // Sync queued requests
  const syncQueue = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsSyncing(false);
      return;
    }

    try {
      const items = await getQueueItems();
      const unsyncedItems = items.filter(i => !i.synced);
      
      let successCount = 0;
      let failCount = 0;

      for (const item of unsyncedItems) {
        try {
          const response = await fetch(`${API_URL}${item.url}`, {
            method: item.method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: item.data ? JSON.stringify(item.data) : undefined
          });

          if (response.ok) {
            await markQueueItemSynced(item.id);
            successCount++;
          } else {
            failCount++;
            // Don't remove failed items - they'll retry next sync
            console.error(`Sync failed for ${item.description}:`, await response.text());
          }
        } catch (err) {
          failCount++;
          console.error(`Sync error for ${item.description}:`, err);
        }
      }

      // Clean up synced items
      await clearSyncedItems();
      await updateQueueCount();

      if (successCount > 0) {
        toast.success(`Synced ${successCount} offline change${successCount > 1 ? 's' : ''}`, { duration: 3000 });
      }
      if (failCount > 0) {
        toast.error(`${failCount} change${failCount > 1 ? 's' : ''} failed to sync`, { duration: 3000 });
      }

      // Sync photos
      await syncPhotos(token);

      setLastSyncTime(new Date());
    } catch (err) {
      console.error('Queue sync error:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, updateQueueCount]);

  // Sync offline photos
  const syncPhotos = async (token) => {
    const photos = await getUnsyncedPhotos();
    
    for (const photo of photos) {
      try {
        const formData = new FormData();
        
        // Convert base64 to blob if needed
        let blob;
        if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
          const response = await fetch(photo.data);
          blob = await response.blob();
        } else {
          blob = photo.data;
        }
        
        formData.append('file', blob, `offline_photo_${photo.id}.jpg`);
        formData.append('work_order_id', photo.work_order_id);
        if (photo.metadata?.description) {
          formData.append('description', photo.metadata.description);
        }

        const response = await fetch(`${API_URL}/api/work-orders/${photo.work_order_id}/images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          await markPhotoSynced(photo.id, result.url || result.firebase_url);
        }
      } catch (err) {
        console.error('Photo sync error:', err);
      }
    }
  };

  // Pre-cache work orders for offline access
  const cacheWorkOrdersForOffline = useCallback(async (workOrders) => {
    await cacheWorkOrders(workOrders);
  }, []);

  // Get cached work orders when offline
  const getOfflineWorkOrders = useCallback(async () => {
    return getCachedWorkOrders();
  }, []);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }
    await syncQueue();
  }, [isOnline, syncQueue]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && queueCount > 0) {
      syncQueue();
    }
  }, [isOnline, queueCount, syncQueue]);

  const value = {
    isOnline,
    queueCount,
    isSyncing,
    lastSyncTime,
    queueRequest,
    syncQueue,
    triggerSync,
    cacheWorkOrdersForOffline,
    getOfflineWorkOrders
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
